import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { useLocation } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined'; 
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import KeyboardDoubleArrowDownOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowDownOutlined';
import KeyboardDoubleArrowUpOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowUpOutlined'; 
import { FormControl, FormControlLabel, Button, Radio, RadioGroup, Tooltip, 
  Snackbar, Alert, IconButton, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Popover} from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LeaveDetector from './LeaveDetector';

import { ProjectContext } from './ProjectContext';
import * as Utils from '../util/Utils'

import '../Dropzone.css';
import NewTargetDialog from '../components/NewTargetDialog';
import EditTargetDialog from '../components/EditTargetDialog';
import SampleTargetCatalogueDialog from '../components/SampleTargetCatalogueDialog';


const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export default function TargetCatalogueView() {
  const queryParams = new URLSearchParams(useLocation().search);
  const projectContext = React.useContext(ProjectContext);
  // merge or replace
  const [addCatalogueAction, setAddCatalogueAction] = React.useState('merge');
  const [openTargetDialog, setOpenTargetDialog] = React.useState(false);
  const [settingAnchorEl, setSettingAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openSnack, setOpenSnack] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [openEditorDialog, setOpenEditorDialog] = React.useState(false);
  const [openSampleCatalogueDialog, setOpenSampleCatalogueDialog] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);

  const { getRootProps, getInputProps, open } = useDropzone
  ({maxFiles: 1, 
    multiple: false,
    accept: { 'text/csv': ['.csv']},
    onDrop: acceptedFiles => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e && e.target) {
          const text = e.target.result as string;
          let targets: any[] = getTargetFileContent(text);
          projectContext.setTargets(targets, projectContext.autoSave);
          if (!projectContext.autoSave) {
            setIsDirty(true);
          }
        }
      }
      reader.readAsText(acceptedFiles[0]);
    }
  });

  const saveTargetCatelogue = () => {
    projectContext.saveTargetFile();
    setIsDirty(false);
  }

  const updateTargetCatalogue = (targets: any[]) => {
    projectContext.setTargets(targets, projectContext.autoSave);
    if (!projectContext.autoSave) {
      setIsDirty(true);
    }
  }

  const getTargetFileContent = (content: string) => {
    if (addCatalogueAction === 'merge') {
      // new targets are added
      // targets which have changed, take the new version
      // comments can't be preserved, will be removed
      const newTargets = Utils.getTargets(content);
      const oldTargets = projectContext.getTargets();

      if (!oldTargets || oldTargets.length === 0) {
        return Utils.getTargets(content);
      }

      for (let target of oldTargets) {
        // add the target to newTargets if it doesn't exist in newTargets
        let targetExist = false;
        for (let newTarget of newTargets) {
          if (target.name === newTarget.name) {
            targetExist = true;
            break;
          }
        }

        if (!targetExist) {
          newTargets.push(target);
        }
      }
      return newTargets;
    } else {
      // replace
      return Utils.getTargets(content);
    }
  }

  const addNewTarget = (newTarget: any) => {
    const targets = projectContext.getTargets();

    let foundTarget = false;
    let different = false;
    for (let target of targets) {
      if (target['name'] === newTarget['name']) {
        foundTarget = true;
        if (target['ra'] !== newTarget['ra']) {
          different = true
          break;
        }
        if (target['dec'] !== newTarget['dec']) {
          different = true
          break;
        }
        if (target['coord_sys'] !== newTarget['coord_sys']) {
          different = true
          break;
        }
      }
    }

    if (foundTarget && different) {
      setOpenSnack(true);
      setErrorMessage('A target with same name but different values exists in catalogue.');
      return;
    }
    
    setOpenTargetDialog(false);

    // add source to catalogue if it's not in there
    if (foundTarget)
      return;

    targets.push(newTarget)
    projectContext.setTargets(targets, projectContext.autoSave);
    if (!projectContext.autoSave) {
      setIsDirty(true);
    }
  }

  const openNewTargetMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenTargetDialog(true);
  };

  const closeNewTargetMenu = () => {
    setOpenTargetDialog(false);
  };

  const closeSampleCatalogueDialog = () => {
    setOpenSampleCatalogueDialog(false);
  }
  const handleOpenSampleCatalogueDialog = () => {
    setOpenSampleCatalogueDialog(true);
  }

  const openSettingMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSettingAnchorEl(event.currentTarget);
  };

  const closeSettingMenu = () => {
    setSettingAnchorEl(null);
  };

  const closeEditorDialog = () => {
    setOpenEditorDialog(false);
  }

  const handleDownload = () => {
    let filename = 'targets.csv';
    var element = document.createElement('a');

    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + projectContext.targetFileContent);
    element.setAttribute('download', filename);

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <LeaveDetector isDirty={isDirty} >
      <Stack spacing={2} height={'calc(100vh - 90px)'}
        sx={{ padding: 1, flexDirection: 'column' }}>
        
        <SampleTargetCatalogueDialog open={openSampleCatalogueDialog} 
          handleClose={closeSampleCatalogueDialog} />
        
        <EditTargetDialog 
          open = {openEditorDialog}
          handleCancel={closeEditorDialog}
          handleSave = {updateTargetCatalogue}
        />

        <Snackbar
          role = "alert"
          aria-label = {"Alert message"}
          open={openSnack}
          autoHideDuration={6000}
          onClose={e => setOpenSnack(false)}
        >
          <Alert severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>

        <Stack direction={'row'} alignItems={'center'} 
          height={'40px'} >
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            <Typography key="project" variant="subtitle1" color="primary">
              {queryParams.get('project')}
            </Typography>

            <Typography key="project" variant="h6" 
              color={ isDirty ? "#c62828" : "primary"}>
              {'Target Catalogue'}
            </Typography>
          </Breadcrumbs>
        </Stack>
        
        <Stack spacing={4} direction={'row'} justifyContent={'space-between'}>
          <div {...getRootProps({ 
            className: 'dropzone',
            'aria-label': 'drag and drop area to upload targets',
            })}>
            <input {...getInputProps()} />
              <Button>
                <Typography variant="caption" textAlign='center'>
                  Drag 'n drop ONE csv file here, or click to select a file to upload Target Catalogue
                </Typography>
              </Button>
          </div>

          <Stack spacing={2} direction={'row'} justifyContent={'flex-end'}
            alignItems={'center'} flexGrow={2}>
            <Tooltip title="Download target catalogue">
                <Button variant="text" onClick={handleDownload}
                  startIcon={<SystemUpdateAltOutlinedIcon />}>
                Download
                </Button>
            </Tooltip>

            <Tooltip title="View sample target catalogue">
              <Button variant="text"
                onClick={handleOpenSampleCatalogueDialog}
                startIcon={<VisibilityIcon />}>
                Sample
              </Button>
            </Tooltip>

            <Button variant="text"
              onClick={openNewTargetMenu}
              startIcon={<ControlPointIcon />}>
              Add
            </Button>

            <NewTargetDialog 
              addTarget={addNewTarget}
              open={openTargetDialog}
              handleClose={closeNewTargetMenu}
              message=''
            />

            <Button variant="text"
              onClick={e => setOpenEditorDialog(true)}
              startIcon={<EditNoteOutlinedIcon />}>
              Edit
            </Button>

            <Button variant="text"
              onClick={saveTargetCatelogue}
              startIcon={<SaveIcon />}>
              Save
            </Button>

            <IconButton aria-label="save" color='primary'
              onClick={openSettingMenu}>
              {
                Boolean(settingAnchorEl)
                ?
                <KeyboardDoubleArrowUpOutlinedIcon/>
                :
                <KeyboardDoubleArrowDownOutlinedIcon />
              }
            </IconButton>

            <Popover
              slotProps={{ paper: { sx: { width: '250px', padding: '20px'  } } }}
              id="menu-appbar"
              anchorEl={settingAnchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: -45,
                horizontal: 'right',
              }}
              open={Boolean(settingAnchorEl)}
              onClose={closeSettingMenu}
            >
              <Typography>When loading catalogue file</Typography>
              <FormControl tabIndex={0} sx={{ display: 'flex', 
                flexDirection: 'row', alignItems: 'center' }} >
                <RadioGroup
                  name="additional-catalogue"
                  value={addCatalogueAction}
                  onChange={e => setAddCatalogueAction(e.target.value)}
                >
                  <FormControlLabel value="replace" control={<Radio />} label="Replace" />
                  <FormControlLabel value="merge" control={<Radio />} label="Merge" />
                </RadioGroup>
              </FormControl>
            </Popover>
          </Stack>
        </Stack>

        <Stack className='load-file-content' overflow={'scroll'} width={'100%'} >              
          <TableContainer component={Paper} sx={{ width: '100%'}}>
            <Table aria-label="simple table">
              <TableHead sx={{ backgroundColor: '#cfd8dc' }}>
                <TableRow>
                  <TableCell width={'20%'} align="center">Name</TableCell>
                  <TableCell width={'20%'} align="center">RA</TableCell>
                  <TableCell width={'20%'} align="center">DEC</TableCell>
                  <TableCell width={'20%'} align="center">Coordinate System</TableCell>
                  <TableCell width={'20%'} align="center">Velocity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projectContext.getTargets().map((target) => (
                  <StyledTableRow key={target['name']}>
                    <TableCell align="center"
                      style={{color: Utils.validateTarget(target)['isNameValid'] ? '' : '#c62828'}}
                    >
                      {target['name']}
                    </TableCell>
                    <TableCell align="center" 
                      style={{color: Utils.validateTarget(target)['isRAValid'] ? '' : '#c62828'}}
                    >
                      {target['ra']}
                    </TableCell>
                    <TableCell align="center" 
                      style={{color: Utils.validateTarget(target)['isDecValid'] ? '' : '#c62828'}}
                    >
                      {target['dec']}
                    </TableCell>
                    <TableCell align="center">
                      {target['coord_sys']||'J2000'}
                    </TableCell>
                    <TableCell align="center">
                      {Utils.getTargetVelocityString(target)}
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Stack>
    </LeaveDetector>
  );
}
